import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { CREATE_ORDERWISE_INTEGRATION, GET_ORDERWISE_INTEGRATIONS } from './queries.ts';
import OrderwiseIntegrationForm from './OrderwiseIntegrationForm.tsx';
import type { OrderwiseFormData } from './types.ts';
import { parseDecimalField } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_ORDERWISE_PATH } from '../../constants.ts';

const FORM_ID = 'orderwise-integration-form';

const OrderwiseIntegrationAddPage = () => {
    const { t } = useTranslation('integrations');
    const navigate = useNavigate();
    const { publishToast } = useToaster();

    const backTooltipText = `${t('backTooltipText')} ${t('orderwise.title')}`;

    const [createIntegration] = useMutation(CREATE_ORDERWISE_INTEGRATION, {
        refetchQueries: [GET_ORDERWISE_INTEGRATIONS],
    });

    const handleCreateIntegration = async (formData: OrderwiseFormData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    username: formData.username,
                    password: formData.password,
                    baseUrl: formData.baseUrl,
                    sessionId: formData.sessionId,
                    priceCheckLowerThresholdPercentage: parseDecimalField(formData.priceCheckLowerThresholdPercentage),
                    priceCheckUpperThresholdPercentage: parseDecimalField(formData.priceCheckUpperThresholdPercentage),
                    integrationParameters: formData.integrationParameters ?? {},
                    proxy: formData.proxy,
                    attachmentsBaseUrl: formData.attachmentsBaseUrl || null,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                },
            });
            publishToast({ description: t('orderwise.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_ORDERWISE_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('orderwise.createError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('orderwise.title'), to: url(CUSTOMER_INTEGRATION_ORDERWISE_PATH) },
                            { label: t('orderwise.form.createTitle') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ORDERWISE_PATH)}>
                                {t('orderwise.form.cancelButton')}
                            </Link>
                        </Button>
                        <Button variant="primary" type="submit" form={FORM_ID}>
                            {t('orderwise.form.createButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <OrderwiseIntegrationForm onSubmit={handleCreateIntegration} formId={FORM_ID} />
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default OrderwiseIntegrationAddPage;
